import { Point } from './Point';
import { Rectangle } from './Rectangle';

let qid = 0;

export class QuadTree {
  id: number;
  boundary: Rectangle;
  capacity: number;
  points: Point[];
  divided: boolean;
  parent: QuadTree | null;
  northeast: QuadTree | null = null;
  northwest: QuadTree | null = null;
  southeast: QuadTree | null = null;
  southwest: QuadTree | null = null;

  constructor(boundary: Rectangle, capacity: number, parent: QuadTree | null) {
    this.id = ++qid;
    this.boundary = boundary;
    this.capacity = capacity;
    this.points = [];
    this.divided = false;
    this.parent = parent;
  }

  subdivide(): void {
    const x = this.boundary.x;
    const y = this.boundary.y;
    const w = this.boundary.w / 2;
    const h = this.boundary.h / 2;

    const ne = new Rectangle(x + w, y - h, w, h);
    this.northeast = new QuadTree(ne, this.capacity, this);
    const nw = new Rectangle(x - w, y - h, w, h);
    this.northwest = new QuadTree(nw, this.capacity, this);
    const se = new Rectangle(x + w, y + h, w, h);
    this.southeast = new QuadTree(se, this.capacity, this);
    const sw = new Rectangle(x - w, y + h, w, h);
    this.southwest = new QuadTree(sw, this.capacity, this);

    this.divided = true;
  }

  insert(point: Point): boolean {
    if (!this.boundary.contains(point)) {
      return false;
    }

    if (!this.divided && this.points.length < this.capacity) {
      this.points.push(point);
      point.quadtree = this;
      return true;
    } else {
      if (!this.divided) {
        this.subdivide();

        const points = this.getAllPoints();
        this.points.length = 0;
        for (const p of points) {
          this.insert(p);
        }
      }

      if (this.northeast?.insert(point)) {
        return true;
      } else if (this.northwest?.insert(point)) {
        return true;
      } else if (this.southeast?.insert(point)) {
        return true;
      } else if (this.southwest?.insert(point)) {
        return true;
      }
    }
    return false;
  }

  remove(point: Point): boolean {
    point.quadtree = null;
    const index = this.points.indexOf(point);
    if (index > -1) {
      this.points.splice(index, 1);
      if (this.parent) this.parent.merge();
      return true;
    }

    if (this.divided) {
      if (this.northeast?.remove(point)) return true;
      if (this.northwest?.remove(point)) return true;
      if (this.southeast?.remove(point)) return true;
      if (this.southwest?.remove(point)) return true;
    }

    return false;
  }

  query(range: Rectangle, found: Point[] = []): Point[] {
    if (!this.boundary.intersects(range)) {
      return found;
    } else {
      for (const p of this.points) {
        if (range.contains(p)) {
          found.push(p);
        }
      }
      if (this.divided) {
        this.northwest?.query(range, found);
        this.northeast?.query(range, found);
        this.southwest?.query(range, found);
        this.southeast?.query(range, found);
      }
    }
    return found.sort((a: Point, b: Point) => a.distance(b));
  }

  merge(): void {
    if (this.divided) {
      const totalPoints = this.getAllPointsLength();

      if (totalPoints < this.capacity) {
        this.points = this.points.concat(this.northeast!.points);
        this.points = this.points.concat(this.northwest!.points);
        this.points = this.points.concat(this.southeast!.points);
        this.points = this.points.concat(this.southwest!.points);

        this.northeast = null;
        this.northwest = null;
        this.southeast = null;
        this.southwest = null;
        this.divided = false;

        for (const p of this.points) {
          p.quadtree = this;
        }
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.divided) {
      this.northeast?.draw(ctx);
      this.northwest?.draw(ctx);
      this.southeast?.draw(ctx);
      this.southwest?.draw(ctx);
    } else {
      ctx.strokeStyle = 'rgba(128, 128, 128, 0.5)';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        this.boundary.x - this.boundary.w,
        this.boundary.y - this.boundary.h,
        this.boundary.w * 2,
        this.boundary.h * 2,
      );
      ctx.font = '10px Arial';
      ctx.fillStyle = 'rgba(128, 128, 128, 0.5)';

      ctx.fillText(`Q${this.id}`, this.boundary.x, this.boundary.y);
      ctx.fillText(
        this.points.length.toString(),
        this.boundary.x,
        this.boundary.y + 10,
      );
    }
  }

  update(): void {
    const allPoints = this.getAllPoints();
    this.clear();
    for (const point of allPoints) {
      this.insert(point);
    }
  }

  getAllPoints(): Point[] {
    let allPoints = [...this.points];
    if (this.divided) {
      allPoints = allPoints.concat(this.northeast!.getAllPoints());
      allPoints = allPoints.concat(this.northwest!.getAllPoints());
      allPoints = allPoints.concat(this.southeast!.getAllPoints());
      allPoints = allPoints.concat(this.southwest!.getAllPoints());
    }
    return allPoints;
  }

  getAllPointsLength(): number {
    let allPointsLength = this.points.length;
    if (this.divided) {
      allPointsLength += this.northeast!.getAllPointsLength();
      allPointsLength += this.northwest!.getAllPointsLength();
      allPointsLength += this.southeast!.getAllPointsLength();
      allPointsLength += this.southwest!.getAllPointsLength();
    }
    return allPointsLength;
  }

  clear(): void {
    this.points = [];
    if (this.divided) {
      this.northeast!.clear();
      this.northwest!.clear();
      this.southeast!.clear();
      this.southwest!.clear();
      this.divided = false;
    }
  }
}
